import { Divider, Grid, IconButton, useMediaQuery } from '@mui/material'
import { Formik } from 'formik'
// lodash removed from this file after performance refactor (merge+keyBy each render was costly for large skill sets)
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo } from 'react'
import { getI18n, useTranslation } from 'react-i18next'
import { getErrMsg, toastNeg, toastPos } from '../../../../common/toastHelper'
import { IDropdownSkill, ISkillInit, ISkillProject, IUserSkills } from '../../../../interfaces/skills/ISkills'
import { useStore } from '../../../../Provider'
import { theme } from '../../../../theme/CustomTheme'
import { MuiButtonVariants, OPButton } from '../../../OPcomponents/OPButton'
import { OPSkillbar } from '../../../OPcomponents/OPSkillbar'
import { OPSkillsCombobox } from '../../../OPcomponents/OPSkillsCombobox'
import { skillSchema } from '../../../OPcomponents/ValidationSchemas'
import '../../../styles/grid.scss'
import { DeleteIconBlack } from '../../../styles/Icons'
import '../../ceretedu.scss'
import '../../profile.scss'

interface AddSkillToProject {
  setProjectSkills: (values: ISkillProject[]) => void
  curProjectSkills: ISkillProject[]
  deleteProjectConfirmation?: boolean
  projectId: number
  isNewProject?: boolean
  isEmployerJobTitlesProject?: boolean
  setProjectFieldValue?: (field: string, value: unknown, shouldValidate?: boolean) => void
}

const AddSkillToProject = (props: AddSkillToProject): JSX.Element => {
  const {
    setProjectSkills,
    curProjectSkills,
    deleteProjectConfirmation,
    projectId,
    isNewProject = false,
    isEmployerJobTitlesProject = false,
    setProjectFieldValue,
  } = props
  const rootStore = useStore()
  const { t, i18n } = useTranslation()
  const { deleteNewSkillProject, postNewSkillProject, allSkills, getAllSkills } = rootStore.skillStore
  const { validatedUser } = rootStore.loginStore
  const shouldWrap = useMediaQuery(theme.breakpoints.down('lg'))

  // Memoize initial form state to keep Formik from unnecessary internal work.
  const skillInit: ISkillInit = useMemo(
    () => ({
      userId: validatedUser,
      skillId: -1,
      skillLevel: 0,
      textFi: '',
      textEng: null,
      comment: '',
    }),
    [validatedUser],
  )

  const { getAllUserSkills, userSkills } = rootStore.personStore

  // Extend dropdown skill type locally to include category for grouping
  type SkillOption = IDropdownSkill & { categoryName?: string }
  // Precompute for O(1) membership checks
  const userSkillIdsSet = useMemo(() => new Set(userSkills.map((s) => s.skillId)), [userSkills])

  // Minimal shape needed from a base skill (inner object of allSkills[x].skill)
  interface MinimalSkill extends Record<string, unknown> {
    skillId: number
    skillNameFi: string
    skillNameEng?: string | null
    categoryName?: string
  }
  type CombinedSkill = MinimalSkill | IUserSkills

  // Combine all skills (base + user) prioritizing user skill metadata without invoking lodash each render.
  const combinedSkills: CombinedSkill[] = useMemo(() => {
    const map = new Map<number, CombinedSkill>()
    // Insert base skills first
    for (const s of allSkills) {
      const base: MinimalSkill = s.skill as unknown as MinimalSkill
      map.set(base.skillId, base)
    }
    // Overwrite with user skill (contains level/comment) when present
    for (const us of userSkills) map.set(us.skillId, us)
    const arr = Array.from(map.values())
    // Ensure user skills appear first while keeping relative order otherwise.
    arr.sort((a, b) => {
      const aUser = userSkillIdsSet.has(a.skillId)
      const bUser = userSkillIdsSet.has(b.skillId)
      if (aUser === bUser) return 0
      return aUser ? -1 : 1
    })
    return arr
  }, [allSkills, userSkills, userSkillIdsSet])

  // Build dropdown options (excluding already added skills) & sort; all memoized.
  const dropdownSkills: SkillOption[] = useMemo(() => {
    const options: SkillOption[] = []
    const language = i18n.language
    const addedIds = new Set(curProjectSkills.map((s) => s.skillId))

    for (const skill of combinedSkills) {
      if (addedIds.has(skill.skillId)) continue
      const hasLevel = (s: CombinedSkill): s is IUserSkills => 'skillLevel' in s
      const hasComment = (s: CombinedSkill): s is IUserSkills => 'comment' in s
      const categoryName = (skill as { categoryName?: string }).categoryName
      options.push({
        key: skill.skillId,
        text: language === 'en' && skill.skillNameEng?.length ? skill.skillNameEng : skill.skillNameFi,
        textFi: skill.skillNameFi,
        textEng: skill.skillNameEng,
        value: skill.skillId.toString(),
        skillLevel: hasLevel(skill) ? skill.skillLevel : 0,
        comment: hasComment(skill) ? skill.comment : '',
        categoryName,
      })
    }

    options.sort((a, b) => {
      const lang = language
      const catA = a.categoryName || ''
      const catB = b.categoryName || ''
      const catCmp = catA.localeCompare(catB, lang, { sensitivity: 'base' })
      if (catCmp !== 0) return catCmp
      const textA = a.text || ''
      const textB = b.text || ''
      return textA.localeCompare(textB, lang, { sensitivity: 'base' })
    })
    return options
  }, [combinedSkills, curProjectSkills, i18n.language])

  const addSkill = useCallback(
    async (values: ISkillInit) => {
      try {
        const skillToAdd: ISkillProject = {
          userId: validatedUser,
          projectId,
          skillId: values.skillId,
          textFi: values.textFi,
          textEng: values.textEng,
          skillLevel: values.skillLevel,
        }
        if (!isNewProject && !setProjectFieldValue) {
          await postNewSkillProject(skillToAdd)
          toastPos(t('Toasts.ProjectSkillAdd'), true)
        }
        setProjectSkills([...curProjectSkills, skillToAdd])
        if (setProjectFieldValue) setProjectFieldValue('userProject.linkedSkills', [...curProjectSkills, skillToAdd])
      } catch (error) {
        toastNeg(getErrMsg(error, t) || t('Toasts.ProjectSkillAddError'), true)
      }
    },
    [validatedUser, projectId, isNewProject, setProjectFieldValue, postNewSkillProject, t, setProjectSkills, curProjectSkills],
  )

  const deleteSkill = useCallback(
    async (skillId: number) => {
      try {
        if (!isNewProject && !setProjectFieldValue) {
          await deleteNewSkillProject(validatedUser, skillId, projectId)
          toastPos(t('Toasts.ProjectSkillDelete'), true)
        }
        setProjectSkills(curProjectSkills.filter((skill) => skill.skillId !== skillId))
        if (setProjectFieldValue)
          setProjectFieldValue(
            'userProject.linkedSkills',
            curProjectSkills.filter((skill) => skill.skillId !== skillId),
          )
      } catch (error) {
        toastNeg(getErrMsg(error, t) || t('Toasts.ProjectSkillDeleteError'), true)
      }
    },
    [isNewProject, setProjectFieldValue, deleteNewSkillProject, validatedUser, projectId, t, setProjectSkills, curProjectSkills],
  )

  useEffect(() => {
    Promise.allSettled([getAllSkills(validatedUser, false), getAllUserSkills(validatedUser, false)])
  }, [getAllSkills, getAllUserSkills, validatedUser])

  return (
    <Grid
      container
      wrap={shouldWrap ? 'wrap' : 'nowrap'}
      columnSpacing={4}
      rowSpacing={4}
      className='gridcontainer--addskilltoproject__componentcontainer'
    >
      <Formik validateOnMount initialValues={skillInit} onSubmit={addSkill} validationSchema={skillSchema}>
        {({ setFieldValue, setValues, values, isSubmitting, resetForm, isValid, validateForm, dirty, submitForm }) => (
          <>
            <Grid item xs={12} lg={6}>
              <Grid container item xs={12} rowSpacing={2}>
                <Grid item xs={12}>
                  <span className='myskill--font__titlefont sini'>
                    {isEmployerJobTitlesProject ? t('Projects.SkillsUsedInEmployerJobTitles') : t('Projects.SkillsUsedInProject')}
                  </span>
                </Grid>
                <Grid item xs={12}>
                  <OPSkillsCombobox
                    name='skillId'
                    disabled={deleteProjectConfirmation}
                    placeholder={`${t('Discussions.GoalSkill')}` + '\u2026'}
                    multiple={false}
                    disableClearable={false}
                    onDelete={() => {
                      // Batch clear related fields in one state update
                      setValues({
                        ...values,
                        textFi: '',
                        textEng: null,
                        skillId: -1,
                        skillLevel: 0,
                        comment: '',
                      })
                    }}
                    curState={dropdownSkills.find((skill) => skill.key === values.skillId) || null}
                    optionList={dropdownSkills}
                    label={`${t('Discussions.GoalSkill')}`}
                    groupBy={(opt) => opt?.categoryName || t('Skills.Category')}
                    onChange={(e, val) => {
                      if (val) {
                        // Single batched update avoids relying on async gaps between multiple setFieldValue calls
                        setValues({
                          ...values,
                          textFi: val.textFi ?? '',
                          textEng: val.textEng ?? null,
                          skillId: val.key ?? -1,
                          skillLevel: val.skillLevel ?? 0,
                          comment: val.comment ?? '',
                        })
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <OPSkillbar
                    readonly={values.skillId === -1}
                    name='skillLvl'
                    hasLabel
                    label={`${t('Skills.Level')}`}
                    value={values.skillLevel ?? undefined}
                    showLevelText={true}
                    onChange={(e, val) => setFieldValue('skillLevel', val)}
                  />
                </Grid>
                <Grid item container xs={12}>
                  <Grid item xs={12}>
                    <p className='profile--titlecaption nopadding'>{t('Skills.SkillOverview')}</p>
                  </Grid>
                  <Grid item xs={12} className='griditem--addskilltoproject__summarycontainer'>
                    <p className='ceretedu--fonttext darker'>
                      {values?.comment?.length > 0 ? values?.comment : t('Skills.NoAddedDescription')}
                    </p>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <OPButton
                    buttonVariant={MuiButtonVariants.secondary_outlined}
                    onClick={async () => {
                      await submitForm()
                      resetForm()
                      validateForm(skillInit)
                    }}
                    disabled={!isValid || deleteProjectConfirmation || isSubmitting}
                    label={t('Buttons.AddSkill')}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Formik>
      <Grid item xs={12} lg>
        <Divider orientation={shouldWrap ? 'horizontal' : 'vertical'} />
      </Grid>
      <Grid item xs={12} lg={6}>
        <Grid container className='gridcontainer--addskilltoproject__addedskills'>
          {curProjectSkills &&
            curProjectSkills.map((skill) => (
              <Grid item key={skill.skillId} xs={12}>
                <Grid container wrap='nowrap' className='gridcontainer--addskilltoproject__skillcontainer'>
                  <Grid xs={12} item className='profile--listitembtn__style'>
                    <span className='myskill--font__personText darker'>
                      {getI18n().language === 'en' && skill.textEng?.length ? skill.textEng : skill.textFi}
                    </span>
                  </Grid>
                  <Grid item>
                    <IconButton
                      className='profile--listitembtn__pointerall'
                      disabled={deleteProjectConfirmation}
                      onClick={() => deleteSkill(skill.skillId)}
                    >
                      <DeleteIconBlack />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            ))}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default observer(AddSkillToProject)
